import type { ReactNode } from 'react';
import { juntarClasses } from './classes';
import { IconeCadeado, IconeCheck } from './icones';
import { useRevelacao } from './movimento';
import {
  DESCRICAO_STATUS_CONTRATO,
  ROTULO_STATUS_CONTRATO,
  STATUS_CONTRATO,
  type StatusContrato,
} from './tipos';

export type EstadoDoPasso = 'concluido' | 'atual' | 'pendente' | 'bloqueado';

export interface Passo {
  id: string;
  rotulo: string;
  descricao?: string;
  estado: EstadoDoPasso;
}

/** Anunciado ao leitor de tela — a cor do marcador não é lida. */
const ESTADO_POR_EXTENSO: Readonly<Record<EstadoDoPasso, string>> = {
  concluido: 'concluída',
  atual: 'etapa atual',
  pendente: 'pendente',
  bloqueado: 'bloqueada',
};

const MARCADOR: Readonly<Record<EstadoDoPasso, string>> = {
  concluido: 'border-acento-ciano bg-acento-ciano text-superficie-base',
  atual: 'border-acento-indigo bg-acento-indigo text-superficie-base ring-4 ring-acento-indigo/20',
  pendente: 'border-borda bg-superficie-elevada text-conteudo-secundario',
  bloqueado: 'border-dashed border-borda bg-superficie-base text-conteudo-secundario',
};

export interface PropsStepper {
  passos: readonly Passo[];
  orientacao?: 'horizontal' | 'vertical';
  /** Nome da sequência, para o `aria-label` da lista. */
  rotulo: string;
  /** Torna cada passo um botão. Sem isto, o stepper é só leitura. */
  aoSelecionar?: (id: string) => void;
  /** Desliga a revelação em cascata. */
  semAnimacao?: boolean;
  className?: string;
}

/**
 * Máquina de estados como passos visuais (§8.1 — "timeline do contrato como
 * stepper animado").
 *
 * Marcador, número e rótulo carregam o estado juntos: quem não distingue as
 * cores lê o ícone e o texto, e o leitor de tela recebe o estado por extenso. O
 * stepper não decide transição — recebe os passos prontos; quem conhece as
 * regras é o domínio.
 */
export function Stepper({
  passos,
  orientacao = 'horizontal',
  rotulo,
  aoSelecionar,
  semAnimacao = false,
  className,
}: PropsStepper): JSX.Element {
  const vertical = orientacao === 'vertical';

  return (
    <ol
      aria-label={rotulo}
      className={juntarClasses(vertical ? 'flex flex-col' : 'flex w-full items-start', className)}
    >
      {passos.map((passo, indice) => (
        <ItemDoStepper
          key={passo.id}
          passo={passo}
          indice={indice}
          total={passos.length}
          anteriorConcluido={passos[indice - 1]?.estado === 'concluido'}
          vertical={vertical}
          aoSelecionar={aoSelecionar}
          semAnimacao={semAnimacao}
        />
      ))}
    </ol>
  );
}

interface PropsItem {
  passo: Passo;
  indice: number;
  total: number;
  anteriorConcluido: boolean;
  vertical: boolean;
  aoSelecionar?: (id: string) => void;
  semAnimacao: boolean;
}

function ItemDoStepper({
  passo,
  indice,
  total,
  anteriorConcluido,
  vertical,
  aoSelecionar,
  semAnimacao,
}: PropsItem): JSX.Element {
  const referencia = useRevelacao<HTMLLIElement>({
    indice,
    direcao: vertical ? 'esquerda' : 'baixo',
    distancia: 8,
    ativo: !semAnimacao,
  });

  const concluido = passo.estado === 'concluido';
  const primeiro = indice === 0;
  const ultimo = indice === total - 1;
  const trilho = concluido ? 'bg-acento-ciano' : 'bg-borda';

  const marcador = (
    <span
      aria-hidden="true"
      className={juntarClasses(
        'relative z-10 flex h-8 w-8 shrink-0 items-center justify-center rounded-full border text-xs font-semibold transition-colors',
        MARCADOR[passo.estado],
      )}
    >
      {concluido ? (
        <IconeCheck className="h-4 w-4" />
      ) : passo.estado === 'bloqueado' ? (
        <IconeCadeado className="h-3.5 w-3.5" />
      ) : (
        indice + 1
      )}
    </span>
  );

  const texto = (
    <span className="block min-w-0">
      <span
        className={juntarClasses(
          'block text-sm font-medium',
          passo.estado === 'pendente' || passo.estado === 'bloqueado'
            ? 'text-conteudo-secundario'
            : 'text-conteudo-primario',
        )}
      >
        {passo.rotulo}
      </span>
      <span className="sr-only"> — {ESTADO_POR_EXTENSO[passo.estado]}</span>
      {passo.descricao !== undefined && (
        <span className="mt-1 block text-xs text-conteudo-secundario">{passo.descricao}</span>
      )}
    </span>
  );

  const interno: ReactNode = vertical ? (
    <>
      {/* Coluna do trilho: o marcador e a linha que desce até o próximo passo. */}
      <span className="flex flex-col items-center self-stretch">
        {marcador}
        {!ultimo && (
          <span aria-hidden="true" className={juntarClasses('my-1 w-px flex-1', trilho)} />
        )}
      </span>
      <span className={juntarClasses('min-w-0 flex-1', ultimo ? 'pt-1' : 'pb-6 pt-1')}>
        {texto}
      </span>
    </>
  ) : (
    <>
      {marcador}
      {texto}
    </>
  );

  const classesInternas = vertical
    ? 'flex w-full gap-3 text-left'
    : 'flex flex-col items-center gap-2 px-2';

  return (
    <li
      ref={referencia}
      aria-current={passo.estado === 'atual' ? 'step' : undefined}
      className={juntarClasses(
        'relative',
        vertical ? 'flex' : 'flex flex-1 flex-col items-center text-center',
      )}
    >
      {/* No horizontal os trilhos ligam os marcadores por cima da linha do topo. */}
      {!vertical && !primeiro && (
        <span
          aria-hidden="true"
          className={juntarClasses(
            'absolute left-0 top-4 h-px w-1/2 -translate-y-1/2',
            anteriorConcluido ? 'bg-acento-ciano' : 'bg-borda',
          )}
        />
      )}
      {!vertical && !ultimo && (
        <span
          aria-hidden="true"
          className={juntarClasses('absolute right-0 top-4 h-px w-1/2 -translate-y-1/2', trilho)}
        />
      )}

      {aoSelecionar === undefined ? (
        <span className={classesInternas}>{interno}</span>
      ) : (
        <button
          type="button"
          onClick={() => aoSelecionar(passo.id)}
          disabled={passo.estado === 'bloqueado'}
          className={juntarClasses(
            classesInternas,
            'rounded-card transition-colors hover:bg-superficie-vidro disabled:cursor-not-allowed disabled:hover:bg-transparent',
          )}
        >
          {interno}
        </button>
      )}
    </li>
  );
}

export interface PropsStepperContrato {
  status: StatusContrato;
  orientacao?: 'horizontal' | 'vertical';
  /** Mostra a explicação de cada estado abaixo do rótulo. */
  comDescricao?: boolean;
  aoSelecionar?: (status: StatusContrato) => void;
  semAnimacao?: boolean;
  className?: string;
}

/**
 * O ciclo do contrato de ICMS Ativo em 5 passos, na ordem do caminho feliz.
 *
 * Reabertura (`→ ABERTO`, permitida a partir de qualquer estado) não vira passo:
 * é a operação de correção do operador, não uma etapa do fluxo. Ela apenas traz
 * o stepper de volta ao começo.
 */
export function StepperContrato({
  status,
  orientacao = 'horizontal',
  comDescricao = false,
  aoSelecionar,
  semAnimacao,
  className,
}: PropsStepperContrato): JSX.Element {
  const atual = STATUS_CONTRATO.indexOf(status);

  const passos: Passo[] = STATUS_CONTRATO.map((valor, indice) => ({
    id: valor,
    rotulo: ROTULO_STATUS_CONTRATO[valor],
    ...(comDescricao ? { descricao: DESCRICAO_STATUS_CONTRATO[valor] } : {}),
    estado: indice < atual ? 'concluido' : indice === atual ? 'atual' : 'pendente',
  }));

  return (
    <Stepper
      passos={passos}
      orientacao={orientacao}
      rotulo="Etapas do contrato"
      semAnimacao={semAnimacao}
      className={className}
      {...(aoSelecionar === undefined
        ? {}
        : { aoSelecionar: (id: string) => aoSelecionar(id as StatusContrato) })}
    />
  );
}
